import { CommonModule } from '@angular/common'
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostBinding, HostListener, inject, Input, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'
import { FieldComponent } from '../field/field.component'

/**
 * @public
 */
export type SelectElement = NgElement & WithProperties<SelectComponent>

/**
 * @public
 */
@Component({
  selector: 'gpei-select',
  templateUrl: './select.component.html',
  styleUrls: [ './select.component.sass' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: true,
  imports: [ CommonModule ]
})
export class SelectComponent extends FieldComponent implements AfterViewInit {

  private readonly _renderer2 = inject(Renderer2)

  @ViewChild('select')
  private readonly _selectRef: ElementRef<HTMLSelectElement>

  @ViewChild('optionsSlot')
  private readonly _optionsSlotRef: ElementRef<HTMLSlotElement>

  @Input()
  @HostBinding()
  options: Record<string, string> = { }

  protected _elementRef = inject(ElementRef)

  /**
   * Lifecycle hook for when the View of the component has finished it's initialization.
   *
   * It will set the validity based on the value of the select element in the shadow DOM.
   */
  ngAfterViewInit(): void {
    // Set the default validity state
    this.syncValidity(this._selectRef?.nativeElement)
  }

  /**
   * Lifecycle hook for the slotted content has been changed.
   *
   * It will move previously slotted content into the shadow DOM, update the selected option,
   * and update the validity.
   */
  onSlottedContentChanged(): void {
    // Update the slotted content
    let slotElement = this._optionsSlotRef.nativeElement

    slotElement.assignedElements().forEach(it => {
      // Move the previously slotted content to the shadow DOM
      this._renderer2.insertBefore(
        slotElement.parentElement,
        it,
        slotElement,
        true
      )
    })

    // Set the default selected option
    this.updateSelectedOption(this.value)

    // Set the default validity state
    this.syncValidity(this._selectRef?.nativeElement)
  }

  /**
   * Lifecycle hook for an input event is emitted from the select element in the shadow DOM.
   */
  onInput(event: Event): void {
    // Pause propagation of change event
    event.stopPropagation()
    let target = event.target as HTMLSelectElement

    // Set the value
    this.writeValue(target.value)

    // Mark it as touched
    this.touched = true

    // Update the validity
    this.syncValidity(target)

    // Detect changes
    this._changeDetectorRef.detectChanges()

    // Resume propagation of change event
    this._elementRef.nativeElement.dispatchEvent(new Event('input', { ...event, bubbles: true }))
  }

  /**
   * Lifecycle hook for when hitting enter or requesting a form to submit from a child element.
   *
   * This will request any existing parent form be submitted when the enter key is pressed. This functionality
   * exists in native elements, and this adds that support for our custom elements as well.
   */
  @HostListener('keydown.enter')
  onSubmit(): void {
    this?.form?.requestSubmit()
  }

  /**
   * Update the selected option based on the value
   *
   * @param value The value of the option we want to be selected
   */
  updateSelectedOption(value: string): void {
    let options = this?._selectRef?.nativeElement?.options ?? []

    if (options) {
      if (value != null) {
        for (let option of options) {
          if (option.value == value) {
            option.selected = true
            break
          }
        }
      }

      else {
        options[0].selected = true
      }
    }
  }

  /**
   * Synchronize the validity of the element with the validity state of the provided element
   *
   * @param element The target element
   */
  private syncValidity(element: HTMLSelectElement): void {
    this.setValidity(
      element?.validity,
      element?.validationMessage,
      element
    )
  }

  @Input()
  @HostBinding()
  set value(value: string) {
    super.value = value
    this.updateSelectedOption(this._value as string)
    this._changeDetectorRef.detectChanges()
    this.syncValidity(this._selectRef?.nativeElement)
  }

  get value(): string {
    return super.value
  }

}

/**
 * @public
 */
export const registerSelectElement = registerCustomElementFactory(SelectComponent)
